import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bikeModelAPI, bikeVariantAPI, BASE_URL } from '../services/api';
import './Brands.css';

const BikeModelDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSpecTab, setActiveSpecTab] = useState(null);
  const [selectedColorIdx, setSelectedColorIdx] = useState(0);

  // Image carousel state
  const [currentImageIdx, setCurrentImageIdx] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Variant sync state
  const [showVariantSyncModal, setShowVariantSyncModal] = useState(false);
  const [previewVariants, setPreviewVariants] = useState([]);
  const [loadingPreviewVariants, setLoadingPreviewVariants] = useState(false);
  const [syncingVariant, setSyncingVariant] = useState(null);
  const [syncingAllVariants, setSyncingAllVariants] = useState(false);
  const [variantSyncError, setVariantSyncError] = useState('');

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    fetchModelDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchModelDetails = async () => {
    try {
      setLoading(true);
      const response = await bikeModelAPI.getById(id);
      setModel(response.data);

      // Set default spec tab
      if (response.data.specs && response.data.specs.length > 0) {
        const categories = [...new Set(response.data.specs.map(s => s.category))];
        setActiveSpecTab(categories[0]);
      }

      setError('');
    } catch (error) {
      console.error('Error fetching model details:', error);
      setError('Failed to load model details');
    } finally {
      setLoading(false);
    }
  };

  // Edit handlers
  const handleEditClick = () => {
    setEditFormData({
      name: model.name || '',
      priceRange: model.priceRange || '',
      price: model.price || '',
      rating: model.rating || '',
      reviewCount: model.reviewCount || '',
      engine: model.engine || '',
      mileage: model.mileage || '',
      power: model.power || '',
      topSpeed: model.topSpeed || '',
      fuelType: model.fuelType || '',
      bodyType: model.bodyType || ''
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      await bikeModelAPI.update(id, editFormData);
      setShowEditModal(false);
      fetchModelDetails();
    } catch (err) {
      console.error('Error updating model:', err);
      setError(err.response?.data?.error || 'Failed to update model');
    } finally {
      setEditLoading(false);
    }
  };

  // Group specs by category
  const getSpecsByCategory = () => {
    if (!model?.specs || model.specs.length === 0) return {};
    return model.specs.reduce((acc, spec) => {
      if (!acc[spec.category]) acc[spec.category] = [];
      acc[spec.category].push(spec);
      return acc;
    }, {});
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${BASE_URL}${imagePath}`;
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    if (typeof price === 'string') {
      if (price.includes('Lakh') || price.includes('Crore') || price.includes('Rs')) {
        return price;
      }
      const numPrice = parseFloat(price.replace(/[^0-9.]/g, ''));
      if (!isNaN(numPrice) && numPrice > 0) {
        if (numPrice >= 10000000) {
          return `Rs. ${(numPrice / 10000000).toFixed(2)} Crore`;
        } else if (numPrice >= 100000) {
          return `Rs. ${(numPrice / 100000).toFixed(2)} Lakh`;
        }
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(numPrice);
      }
      return price;
    }
    return '-';
  };

  // Gallery carousel navigation
  const nextImage = useCallback(() => {
    if (model?.images && model.images.length > 0) {
      setCurrentImageIdx(prev => (prev + 1) % model.images.length);
    }
  }, [model?.images]);

  const prevImage = useCallback(() => {
    if (model?.images && model.images.length > 0) {
      setCurrentImageIdx(prev => (prev - 1 + model.images.length) % model.images.length);
    }
  }, [model?.images]);

  // Touch handlers for swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextImage();
    } else if (isRightSwipe) {
      prevImage();
    }
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showLightbox) return;
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') setShowLightbox(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showLightbox, nextImage, prevImage]);

  // Variant sync handlers
  const handleOpenVariantSync = async () => {
    setShowVariantSyncModal(true);
    setVariantSyncError('');
    setPreviewVariants([]);
    setLoadingPreviewVariants(true);

    try {
      const response = await bikeVariantAPI.preview({ bikeSlug: model.slug });
      setPreviewVariants(response.data.variants || []);
    } catch (err) {
      setVariantSyncError(err.response?.data?.error || 'Failed to fetch variants');
    } finally {
      setLoadingPreviewVariants(false);
    }
  };

  const handleSyncSingleVariant = async (variant) => {
    setSyncingVariant(variant.slug);
    try {
      await bikeVariantAPI.syncSingle({
        variantId: variant.variantId,
        bikeId: variant.bikeId,
        name: variant.name,
        slug: variant.slug,
        price: variant.price,
        url: variant.url
      });
      setPreviewVariants(prev => prev.map(v =>
        v.slug === variant.slug ? { ...v, existsInDb: true } : v
      ));
      fetchModelDetails();
    } catch (err) {
      setVariantSyncError(err.response?.data?.error || 'Failed to sync variant');
    } finally {
      setSyncingVariant(null);
    }
  };

  const handleSyncAllVariants = async () => {
    setSyncingAllVariants(true);
    try {
      await bikeVariantAPI.sync({ bikeSlug: model.slug });
      const response = await bikeVariantAPI.preview({ bikeSlug: model.slug });
      setPreviewVariants(response.data.variants || []);
      fetchModelDetails();
    } catch (err) {
      setVariantSyncError(err.response?.data?.error || 'Failed to sync variants');
    } finally {
      setSyncingAllVariants(false);
    }
  };

  if (loading) {
    return (
      <div className="brands-page">
        <div className="loading">Loading model details...</div>
      </div>
    );
  }

  if (error || !model) {
    return (
      <div className="brands-page">
        <div className="alert alert-error">{error || 'Model not found'}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/bike-models')}>
          Back to Models
        </button>
      </div>
    );
  }

  const specsByCategory = getSpecsByCategory();
  const specCategories = Object.keys(specsByCategory);
  const colors = model.colors || [];
  const galleryImages = model.images || [];

  return (
    <div className="brands-page">
      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Bike Model</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Model Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={editFormData.name}
                    onChange={handleEditFormChange}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Price Range</label>
                    <input
                      type="text"
                      name="priceRange"
                      value={editFormData.priceRange}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 1.5 - 2.0 Lakh"
                    />
                  </div>
                  <div className="form-group">
                    <label>Price</label>
                    <input
                      type="text"
                      name="price"
                      value={editFormData.price}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 1.5 Lakh"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Rating</label>
                    <input
                      type="text"
                      name="rating"
                      value={editFormData.rating}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 4.5"
                    />
                  </div>
                  <div className="form-group">
                    <label>Review Count</label>
                    <input
                      type="text"
                      name="reviewCount"
                      value={editFormData.reviewCount}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 150"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Engine</label>
                    <input
                      type="text"
                      name="engine"
                      value={editFormData.engine}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 124.73 cc"
                    />
                  </div>
                  <div className="form-group">
                    <label>Mileage</label>
                    <input
                      type="text"
                      name="mileage"
                      value={editFormData.mileage}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 65 kmpl"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Power</label>
                    <input
                      type="text"
                      name="power"
                      value={editFormData.power}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 10.9 bhp"
                    />
                  </div>
                  <div className="form-group">
                    <label>Top Speed</label>
                    <input
                      type="text"
                      name="topSpeed"
                      value={editFormData.topSpeed}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 104 kmph"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Fuel Type</label>
                    <select name="fuelType" value={editFormData.fuelType} onChange={handleEditFormChange}>
                      <option value="">Select Fuel Type</option>
                      <option value="Petrol">Petrol</option>
                      <option value="Electric">Electric</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Body Type</label>
                    <select name="bodyType" value={editFormData.bodyType} onChange={handleEditFormChange}>
                      <option value="">Select Body Type</option>
                      <option value="Sports">Sports</option>
                      <option value="Commuter">Commuter</option>
                      <option value="Cruiser">Cruiser</option>
                      <option value="Adventure">Adventure</option>
                      <option value="Scooter">Scooter</option>
                      <option value="Street">Street</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={editLoading}>
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Variant Sync Modal */}
      {showVariantSyncModal && (
        <div className="modal-overlay">
          <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Sync Variants for {model.name}</h2>
              <button className="modal-close" onClick={() => setShowVariantSyncModal(false)}>&times;</button>
            </div>
            <div className="modal-body sync-modal-body">
              {loadingPreviewVariants && (
                <div className="sync-progress">
                  <div className="spinner"></div>
                  <p>Loading variants from BikeDekho...</p>
                </div>
              )}
              {variantSyncError && (
                <div className="sync-error">
                  <div className="error-icon">&#10005;</div>
                  <h3>Error</h3>
                  <p>{variantSyncError}</p>
                </div>
              )}
              {!loadingPreviewVariants && !variantSyncError && previewVariants.length > 0 && (
                <div className="preview-brands-grid preview-models-grid">
                  {previewVariants.map((variant) => (
                    <div key={variant.slug} className="preview-brand-card preview-model-card">
                      <div className="preview-brand-info preview-model-info">
                        <h4>{variant.name}</h4>
                        {variant.price && <span className="model-price">{variant.price}</span>}
                      </div>
                      <button
                        className={`btn btn-sm ${variant.existsInDb ? 'btn-update' : 'btn-add'}`}
                        onClick={() => handleSyncSingleVariant(variant)}
                        disabled={syncingVariant === variant.slug}
                      >
                        {syncingVariant === variant.slug ? 'Syncing...' : (variant.existsInDb ? 'Update' : 'Add')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {!loadingPreviewVariants && !variantSyncError && previewVariants.length === 0 && (
                <div className="empty-state">
                  <p>No variants found for this model.</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowVariantSyncModal(false)}>
                Close
              </button>
              <button
                className="btn btn-sync"
                onClick={handleSyncAllVariants}
                disabled={syncingAllVariants || loadingPreviewVariants || previewVariants.length === 0}
              >
                {syncingAllVariants ? 'Syncing All...' : 'Sync All Variants'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <div className="breadcrumb">
          <button className="btn btn-link" onClick={() => navigate('/bike-models')}>
            Bike Models
          </button>
          <span className="breadcrumb-separator">/</span>
          <span>{model.name}</span>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={handleEditClick}>
            Edit Model
          </button>
        </div>
      </div>

      {/* Gallery Lightbox Modal */}
      {showLightbox && model.images && model.images.length > 0 && (
        <div className="lightbox-overlay" onClick={() => setShowLightbox(false)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setShowLightbox(false)}>&times;</button>
            <button className="lightbox-nav lightbox-prev" onClick={prevImage}>&#10094;</button>
            <div
              className="lightbox-image-container"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              {model.images[currentImageIdx] && (
                <img
                  src={getImageUrl(model.images[currentImageIdx].image)}
                  alt={model.images[currentImageIdx].alt || `${model.name} image`}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
            </div>
            <button className="lightbox-nav lightbox-next" onClick={nextImage}>&#10095;</button>
            <div className="lightbox-caption">
              <span>{model.images[currentImageIdx]?.alt || `${model.name} - Image ${currentImageIdx + 1}`}</span>
              <span className="lightbox-counter">{currentImageIdx + 1} / {model.images.length}</span>
            </div>
          </div>
        </div>
      )}

      <div className="details-container">
        {/* Main Info Card */}
        <div className="details-card details-main">
          <div className="details-image-section">
            <div className="details-image">
              {(colors[selectedColorIdx]?.image || model.image) ? (
                <img
                  src={getImageUrl(colors[selectedColorIdx]?.image || model.image)}
                  alt={model.name}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="no-image-placeholder large">No Image Available</div>
              )}
            </div>

            {/* Color Swatches */}
            {colors.length > 0 && (
              <div className="details-colors">
                <h4>Available Colors ({colors.length})</h4>
                <div className="color-swatches-large">
                  {colors.map((color, idx) => {
                    const isSelected = selectedColorIdx === idx;
                    let colorHex = '#888';
                    if (color.code) {
                      try {
                        const parsed = JSON.parse(color.code);
                        colorHex = Array.isArray(parsed) ? parsed[0] : `#${color.code.replace('#', '')}`;
                      } catch {
                        colorHex = `#${color.code.replace('#', '')}`;
                      }
                    }

                    return (
                      <div
                        key={idx}
                        className={`color-swatch-large ${isSelected ? 'selected' : ''}`}
                        onClick={() => setSelectedColorIdx(idx)}
                      >
                        <div
                          className="color-circle"
                          style={{ backgroundColor: colorHex }}
                        />
                        <span className="color-name">{color.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="details-info-section">
            <div className="details-header">
              <h1>{model.name}</h1>
              <p className="details-subtitle">{model.brand?.brandName}</p>
            </div>

            <div className="details-price-section">
              <div className="price-box">
                <span className="price-label">Price Range</span>
                <span className="price-value primary">{formatPrice(model.priceRange || model.price)}</span>
              </div>
            </div>

            <div className="details-quick-specs">
              {model.engine && (
                <div className="quick-spec">
                  <span className="spec-icon">&#9889;</span>
                  <span>{model.engine}</span>
                </div>
              )}
              {model.mileage && (
                <div className="quick-spec">
                  <span className="spec-icon">&#128663;</span>
                  <span>{model.mileage}</span>
                </div>
              )}
              {model.power && (
                <div className="quick-spec">
                  <span className="spec-icon">&#9881;</span>
                  <span>{model.power}</span>
                </div>
              )}
            </div>

            {/* Ratings */}
            {(model.rating || model.reviewCount) && (
              <div className="details-ratings">
                {model.rating && (
                  <div className="rating-box">
                    <span className="rating-value">{model.rating}</span>
                    <span className="rating-label">Rating</span>
                  </div>
                )}
                {model.reviewCount && (
                  <div className="rating-box">
                    <span className="rating-value">{model.reviewCount}</span>
                    <span className="rating-label">Reviews</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Specifications Card */}
        {specCategories.length > 0 && (
          <div className="details-card">
            <h3>Specifications</h3>
            <div className="category-tabs">
              {specCategories.map(category => (
                <button
                  key={category}
                  className={`category-tab ${activeSpecTab === category ? 'active' : ''}`}
                  onClick={() => setActiveSpecTab(category)}
                >
                  {category}
                </button>
              ))}
            </div>
            <div className="specs-grid">
              {activeSpecTab && specsByCategory[activeSpecTab]?.map((spec, idx) => (
                <div key={idx} className="spec-row">
                  <span className="spec-label">{spec.specName}</span>
                  <span className="spec-value">{spec.specValue || '-'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Variants Card */}
        <div className="details-card">
          <div className="card-header-with-action">
            <h3>Variants ({model.variants?.length || 0})</h3>
            <button className="btn btn-sync btn-sm" onClick={handleOpenVariantSync}>
              Sync Variants
            </button>
          </div>
          {model.variants && model.variants.length > 0 ? (
            <div className="variants-list">
              {model.variants.map((variant) => (
                <div
                  key={variant.id}
                  className="variant-list-item"
                  onClick={() => navigate(`/bike-variants/${variant.id}`)}
                >
                  <div className="variant-list-info">
                    <h4>{variant.name}</h4>
                  </div>
                  <div className="variant-list-price">
                    {formatPrice(variant.price)}
                  </div>
                  <div className="variant-list-arrow">&#8250;</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>No variants found. Click "Sync Variants" to fetch from BikeDekho.</p>
            </div>
          )}
        </div>

        {/* Gallery Card with Carousel */}
        {galleryImages.length > 0 && (
          <div className="details-card">
            <h3>Gallery ({galleryImages.length})</h3>
            <div className="gallery-carousel-container">
              {/* Main Gallery Image */}
              <div
                className="gallery-carousel"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                <div className="gallery-main-image" onClick={() => setShowLightbox(true)}>
                  <img
                    src={getImageUrl(galleryImages[currentImageIdx]?.image)}
                    alt={galleryImages[currentImageIdx]?.alt || `${model.name} image`}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="gallery-expand-icon">&#x26F6;</div>
                </div>

                {/* Navigation Arrows */}
                {galleryImages.length > 1 && (
                  <>
                    <button className="gallery-nav gallery-prev" onClick={prevImage}>&#10094;</button>
                    <button className="gallery-nav gallery-next" onClick={nextImage}>&#10095;</button>
                  </>
                )}

                {/* Image Counter */}
                <div className="gallery-counter">
                  {currentImageIdx + 1} / {galleryImages.length}
                </div>
              </div>

              {/* Thumbnail Strip */}
              <div className="gallery-thumbnails">
                {galleryImages.map((img, idx) => (
                  <div
                    key={idx}
                    className={`gallery-thumb ${currentImageIdx === idx ? 'active' : ''}`}
                    onClick={() => setCurrentImageIdx(idx)}
                  >
                    <img
                      src={getImageUrl(img.image)}
                      alt={img.alt || `Thumbnail ${idx + 1}`}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Additional Info Card */}
        <div className="details-card">
          <h3>Additional Information</h3>
          <div className="specs-grid">
            <div className="spec-row">
              <span className="spec-label">Model ID</span>
              <span className="spec-value">{model.modelId || model.id}</span>
            </div>
            <div className="spec-row">
              <span className="spec-label">Slug</span>
              <span className="spec-value">{model.slug}</span>
            </div>
            {model.modelUrl && (
              <div className="spec-row">
                <span className="spec-label">Source URL</span>
                <span className="spec-value">
                  <a href={`https://www.bikedekho.com/${model.modelUrl}`} target="_blank" rel="noopener noreferrer">
                    View on BikeDekho
                  </a>
                </span>
              </div>
            )}
            <div className="spec-row">
              <span className="spec-label">Created At</span>
              <span className="spec-value">{new Date(model.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="spec-row">
              <span className="spec-label">Updated At</span>
              <span className="spec-value">{new Date(model.updatedAt).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BikeModelDetails;
